import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useParams } from 'next/navigation'
import { ReactElement } from 'react'

import { useFlags } from '@core/shared/ui/FlagsProvider'

import { TreeBlock, getCardChild } from '../../libs/block'
import { useEditor } from '../../libs/EditorProvider'
import { useJourney } from '../../libs/JourneyProvider'
import { getJourneyRTL } from '../../libs/rtl'
import { AiChatButton } from '../AiChatButton'
import {
  getFooterMobileHeight,
  getTitle,
  hasAiChatButton,
  hasChatWidget,
  hasCombinedFooter,
  hasHostAvatar,
  hasHostDetails
} from '../Card/utils/getFooterElements'
import { StepFields } from '../Step/__generated__/StepFields'
import { InformationButton } from '../StepHeader/InformationButton'

import { ChatButtons } from './ChatButtons'
import { FooterButtonList } from './FooterButtonList'
import { HostAvatars } from './HostAvatars'
import { HostTitleLocation } from './HostTitleLocation'

interface StepFooterProps {
  onFooterClick?: () => void
  sx?: SxProps
  selectedStep?: TreeBlock<StepFields> | null
}

export function StepFooter({
  onFooterClick,
  sx,
  selectedStep: selectedStepProp
}: StepFooterProps): ReactElement {
  const { journey, renderMode } = useJourney()
  const { rtl } = getJourneyRTL(journey)
  const {
    state: { selectedStep: editorSelectedStep }
  } = useEditor()

  const selectedStep =
    selectedStepProp !== undefined ? selectedStepProp : editorSelectedStep

  const card = getCardChild(selectedStep)

  const flags = useFlags()
  const hostAvatar = hasHostAvatar({ journey, renderMode })
  const hostDetails = hasHostDetails({ journey })
  const chat = hasChatWidget({ journey, renderMode })
  const aiChat =
    hasAiChatButton({ journey, renderMode, card }) &&
    flags.apologistChat === true
  const title = getTitle({ journey })

  const footerMobileHeight = getFooterMobileHeight({
    journey,
    renderMode,
    card
  })
  const combinedFooter = hasCombinedFooter({ journey, renderMode })

  const { menuButtonIcon } = journey ?? {}
  const hasMenuButtonIcon = menuButtonIcon != null

  const menuStepBlockId = journey?.menuStepBlock?.id
  const urlParams = useParams()
  const currentPath = urlParams?.['stepSlug']

  const isWebsite = journey?.website === true
  const isMenu =
    menuStepBlockId === currentPath || selectedStep?.id === menuStepBlockId

  return (
    <Box
      data-testid="JourneysStepFooter"
      sx={{
        position: { xs: 'absolute', lg: 'relative' },
        zIndex: 1,
        bottom: 0,
        width: { xs: '100%', lg: 'auto' },
        ...sx
      }}
      onClick={(e) => {
        if (onFooterClick != null) {
          e.stopPropagation()
          onFooterClick()
        }
      }}
    >
      <Stack
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          px: { xs: renderMode === 'default' ? 6 : 3, lg: 0 },
          py: { xs: 2, lg: 0 },
          flexDirection: { lg: rtl ? 'row-reverse' : 'row' },
          alignItems: { xs: 'flex-start', lg: 'center' },
          width: '100%'
        }}
      >
        {!isWebsite && !combinedFooter && (
          <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
            <FooterButtonList />
          </Box>
        )}

        <Stack
          sx={{
            gap: 4,
            width: '100%',
            height: { xs: footerMobileHeight, sm: 52 },
            flexDirection: rtl ? 'row-reverse' : 'row',
            alignItems: 'center',

            justifyContent:
              isWebsite && isMenu && hasMenuButtonIcon
                ? 'space-between'
                : 'flex-end',

            mt: '0px !important'
          }}
        >
          {!isWebsite && combinedFooter && (
            <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
              <FooterButtonList />
            </Box>
          )}
          {isWebsite && isMenu && hasMenuButtonIcon && (
            <Box>
              <InformationButton sx={{ p: 0 }} />
            </Box>
          )}
          {!isWebsite && (
            <Stack
              sx={{
                gap: 2,
                width: '100%',
                minWidth: 0,
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              {hostAvatar && (
                <HostAvatars
                  hasPlaceholder={renderMode === 'admin'}
                  avatarSrc1={journey?.host?.src1}
                  avatarSrc2={journey?.host?.src2}
                />
              )}
              {(title != null || hostDetails) && (
                <Stack sx={{ flex: '1 1 100%', minWidth: 0 }}>
                  {title != null && (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        zIndex: 1,
                        // Always dark mode on lg breakpoint
                        color: { xs: 'primary.main', lg: 'white' },
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {title}
                    </Typography>
                  )}
                  {hostDetails && <HostTitleLocation />}
                </Stack>
              )}

              <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <FooterButtonList />
              </Box>
            </Stack>
          )}

          {(chat || aiChat) && (
            // One group: the AI chat button is a sibling of the
            // MessagePlatform buttons with the same 8px spacing, not a
            // separate box pushed out by the row's larger gap.
            <Stack
              direction={rtl ? 'row-reverse' : 'row'}
              sx={{
                alignItems: 'center',
                gap: 2
              }}
            >
              {chat && <ChatButtons />}
              {aiChat && <AiChatButton />}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}
